/*
 * CAPTURE-OWNED MODULE
 * DocumentSourcesPage — /settings/ingestion.
 *
 * The connection surface for cloud ingestion channels: what is connected, where
 * it is watching, whether it is healthy, how often it is checked, and what
 * happens to a document after capture. Also the landing page for a completed
 * OAuth flow — connectionUiUrl() in capture-service redirects here with
 * ?connection=success|error, which until this page existed was a blank screen.
 *
 * ONE MICROSOFT 365 CONNECTION PER WORKSPACE, shown as a card above the sources.
 * Connect, Reconnect and Disconnect are on that card and nowhere else. A source — a OneDrive
 * folder, an Outlook mailbox — is added to the connection with one request and
 * no trip to Microsoft, and has no sign-in of its own to reconnect.
 *
 * MEMBER-VISIBLE, ADMIN-CONTROLLED. Showing a member that documents arrive from
 * /Scans - YSoft is not the same as letting them enumerate the connecting
 * admin's personal OneDrive, so the list is open and every mutation is hidden
 * behind useIsAdmin. That is hide-for-clarity only: accessPolicy's
 * 'ingestion:channel-folders' and 'ingestion:oauth-start' rules are what
 * actually enforce it, and a member who forges the request still gets a 403.
 *
 * NOTHING HERE IS HARDCODED THAT THE SERVER OWNS. The frequency options and
 * their labels come from GET /providers, and the disposition controls are driven
 * by the registry's capabilities flags rather than by a channel_type check — the
 * registry exists so that "OneDrive cannot move files yet" is data this page
 * reads, not a special case it remembers.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { captureAPI } from '../services/api';
import { useIsAdmin } from '../identity/useIsAdmin';
import CloudFolderPickerDialog from '../components/CloudFolderPickerDialog';
import MailboxStepDialog from '../components/MailboxStepDialog';
import {
  NON_CLOUD_CHANNEL_TYPES,
  presentHealth,
  isMailChannel,
  presentMailbox,
  usesM365Connection,
  isConnectionUsable,
  presentConnectionHealth,
  ACCEPTED_MIME_TYPE_OPTIONS,
} from '../constants/cloudChannels';
import './DocumentSourcesPage.css';

/**
 * The retention periods offered. The validator accepts any positive integer, so
 * this is a UI choice and can widen later without a server change.
 */
const DELETE_AFTER_DAYS_OPTIONS = [7, 14, 30, 60, 90];

/** "3 minutes ago". Coarse on purpose — this is reassurance, not telemetry. */
export function formatRelative(iso, now = Date.now()) {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const seconds = Math.round((now - then) / 1000);
  if (seconds < 0) return 'just now';
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** 'success' | 'error' | null, from ?connection=. Anything else is ignored. */
function readConnectionStatus() {
  try {
    const value = new URLSearchParams(window.location.search).get('connection');
    return value === 'success' || value === 'error' ? value : null;
  } catch {
    return null;
  }
}

/**
 * WHY a connection failed, when the callback is willing to say.
 *
 * It says for exactly one cause: the admin signed in to Microsoft as a
 * different account than the one this workspace is connected as. Every other
 * failure is deliberately indistinguishable. An ALLOWLIST, so a value this
 * build has not heard of renders as the generic failure rather than as text
 * taken from a URL.
 */
const CONNECTION_FAILURE_REASONS = ['different_account'];

export function readConnectionFailureReason() {
  try {
    const value = new URLSearchParams(window.location.search).get('reason');
    return CONNECTION_FAILURE_REASONS.includes(value) ? value : null;
  } catch {
    return null;
  }
}

/** True when the connection was made but the existing sources could not be attached to it. */
function readSourcesUnattached() {
  try {
    return new URLSearchParams(window.location.search).get('sources') === 'unattached';
  } catch {
    return false;
  }
}

/**
 * Remove ONLY the params we put there.
 *
 * connectionUiUrl() appends with '?' or '&' depending on whether
 * INGESTION_CONNECTION_UI_URL already carries a query string, so ours can arrive
 * alongside others that are not ours to discard.
 */
function stripConnectionParam() {
  try {
    const url = new URL(window.location.href);
    const ours = ['connection', 'reason', 'sources', 'rebound', 'channel_id']
      .filter((k) => url.searchParams.has(k));
    if (ours.length === 0) return;
    for (const k of ours) url.searchParams.delete(k);
    const qs = url.searchParams.toString();
    window.history.replaceState({}, '', `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`);
  } catch {
    /* no history / no URL — the banner simply survives a refresh */
  }
}

export default function DocumentSourcesPage() {
  const isAdmin = useIsAdmin();

  const [providers, setProviders] = useState([]);
  const [intervals, setIntervals] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  /**
   * Per-channel activity: what the source last took in and how many documents
   * from it are held awaiting a duplicate decision. Keyed by channel id.
   *
   * A SEPARATE FETCH, AND DELIBERATELY NON-BLOCKING. Each entry aggregates over
   * a ledger and counts documents, so it is neither cheap enough nor important
   * enough to hold the page behind: the card renders complete without it and the
   * two lines fill in when the answers arrive. A channel whose activity read
   * fails simply keeps its placeholder — the settings page's real job is the
   * connection controls, and a ledger hiccup must not take those away.
   */
  const [activity, setActivity] = useState({});

  // Per-row transient state, keyed by channel id, so one card's error or "Saved"
  // never appears on another's.
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  const [pickerChannel, setPickerChannel] = useState(null);
  /** The channel whose Mailbox step is open, or null. */
  const [mailboxChannel, setMailboxChannel] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  /**
   * The tenant's Microsoft 365 connection. null = not connected.
   * `connectionKnown` is false only when the read itself failed: then the page
   * does not know, and must neither invite a connect that may be a reconnect
   * nor disable the sources on a guess.
   */
  const [connection, setConnection] = useState(null);
  const [connectionKnown, setConnectionKnown] = useState(false);
  /** The explain-then-go dialog for Connect / Reconnect. */
  const [m365DialogOpen, setM365DialogOpen] = useState(false);
  const [m365Starting, setM365Starting] = useState(false);
  const [m365Error, setM365Error] = useState(null);
  /**
   * The disconnect confirmation. `preview` is null while the list of sources it
   * will turn off is loading; the confirm button stays disabled until it has
   * arrived, so nobody confirms a list they have not been shown.
   */
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectPreview, setDisconnectPreview] = useState(null);
  const [disconnectError, setDisconnectError] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  /** True once a disconnect has completed on this page load. */
  const [justDisconnected, setJustDisconnected] = useState(false);
  const [connectingId, setConnectingId] = useState(null);
  const [connectError, setConnectError] = useState(null);

  // Read before the effect that strips it, so the first render already knows.
  const [banner] = useState(readConnectionStatus);
  const [failureReason] = useState(readConnectionFailureReason);
  const [sourcesUnattached] = useState(readSourcesUnattached);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // The connection is read ALONGSIDE the other two and may fail on its own.
      // A page that cannot say whether Microsoft 365 is connected still has
      // sources to show, so its failure is caught here rather than allowed to
      // fail the whole load.
      const [providerRes, channelRes, connectionRes] = await Promise.all([
        captureAPI.ingestion.listProviders(),
        captureAPI.ingestion.listChannels(),
        captureAPI.ingestion.getConnection().catch(() => null),
      ]);
      const known = Boolean(connectionRes && connectionRes.ok === true);
      setConnectionKnown(known);
      setConnection(known ? connectionRes.connection || null : null);
      setProviders(Array.isArray(providerRes?.providers) ? providerRes.providers : []);
      setIntervals(
        Array.isArray(providerRes?.enumeration_intervals) ? providerRes.enumeration_intervals : [],
      );
      setChannels(Array.isArray(channelRes?.channels) ? channelRes.channels : []);
    } catch (err) {
      setLoadError(err?.response?.data?.error || 'Could not load document sources.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Strip the param once, after the banner has been captured into state. A
  // refresh must not re-announce a connection that happened minutes ago.
  useEffect(() => {
    stripConnectionParam();
  }, []);

  const cloudChannels = useMemo(
    () => channels.filter((c) => !NON_CLOUD_CHANNEL_TYPES.includes(c.channel_type)),
    [channels],
  );

  /**
   * Fill in the activity lines once the channel list is known.
   *
   * PARALLEL ACROSS CHANNELS, and each one settles on its own: Promise.allSettled
   * rather than Promise.all so one failing channel does not blank the lines on
   * every other card. A rejection leaves that id absent from the map, which is
   * the same state as "not answered yet" and renders the placeholder.
   *
   * `cancelled` guards the unmount: the fetches outlive a navigation away and
   * setting state afterwards is a warning at best and a leak at worst.
   */
  useEffect(() => {
    // Active sources only. One that was turned off takes nothing in, and its
    // card shows no activity lines to fill.
    const ids = cloudChannels.filter((c) => c.is_active !== false).map((c) => c.id);
    if (ids.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      const settled = await Promise.allSettled(
        ids.map((id) => captureAPI.ingestion.channelActivity(id)),
      );
      if (cancelled) return;
      const next = {};
      settled.forEach((outcome, i) => {
        if (outcome.status !== 'fulfilled') return;
        const body = outcome.value;
        if (!body || body.ok !== true) return;
        next[ids[i]] = {
          lastItem: body.last_item || null,
          duplicatesHeld: Number.isFinite(body.duplicates_held) ? body.duplicates_held : 0,
        };
      });
      setActivity((prev) => ({ ...prev, ...next }));
    })();

    return () => { cancelled = true; };
    // Keyed on the ids rather than on the array identity: loadAll replaces the
    // channels array on every save, and depending on the array itself would
    // re-fetch every card's activity after every toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloudChannels.filter((c) => c.is_active !== false).map((c) => c.id).join(',')]);

  const providerFor = useCallback(
    (channelType) => providers.find((p) => p.id === channelType) || null,
    [providers],
  );

  const setRowError = (channelId, message) =>
    setRowErrors((prev) => ({ ...prev, [channelId]: message }));

  /** One PATCH, one card. The response's channel row replaces the local one. */
  const patchChannel = useCallback(async (channelId, patch) => {
    setSavingId(channelId);
    setSavedId(null);
    setRowError(channelId, null);
    try {
      const res = await captureAPI.ingestion.updateChannel(channelId, patch);
      if (res?.channel) {
        setChannels((prev) => prev.map((c) => (c.id === channelId ? { ...c, ...res.channel } : c)));
      }
      setSavedId(channelId);
      setTimeout(() => setSavedId((id) => (id === channelId ? null : id)), 2000);
    } catch (err) {
      // The server's own string, not a paraphrase. Its 400s explain the
      // cross-field rule ("delete_after_days requires move_processed …") better
      // than anything this page could invent, and its 404 is deliberately
      // indistinguishable between "gone" and "not yours".
      setRowError(channelId, err?.response?.data?.error || 'Could not save that change.');
    } finally {
      setSavingId((id) => (id === channelId ? null : id));
    }
  }, []);

  /**
   * Connect, or reconnect, Microsoft 365. One call, no arguments: the server
   * decides which from whether the tenant already has a connection.
   */
  const handleM365Start = useCallback(async () => {
    setM365Starting(true);
    setM365Error(null);
    try {
      const res = await captureAPI.ingestion.startConnection();
      if (res?.authorize_url) {
        // Leaves the SPA entirely — Microsoft brings the browser back to this
        // page with ?connection=.
        window.location.assign(res.authorize_url);
        return;
      }
      setM365Error('The connection could not be started.');
    } catch (err) {
      setM365Error(err?.response?.data?.error || 'The connection could not be started.');
    } finally {
      setM365Starting(false);
    }
  }, []);

  /** Open the confirmation and load what it has to show. */
  const openDisconnect = useCallback(async () => {
    setDisconnectOpen(true);
    setDisconnectPreview(null);
    setDisconnectError(null);
    try {
      const res = await captureAPI.ingestion.previewDisconnect();
      if (res?.ok !== true) throw new Error('no preview');
      setDisconnectPreview({
        account: res.account || null,
        sources: Array.isArray(res.sources) ? res.sources : [],
        // Publish destinations that write through the connection (OneDrive).
        destinations: Array.isArray(res.destinations) ? res.destinations : [],
      });
    } catch (err) {
      setDisconnectError(
        err?.response?.data?.error || 'Could not load what disconnecting would turn off.',
      );
    }
  }, []);

  /**
   * Disconnect. The list is REFETCHED afterwards rather than patched: the
   * connection is gone, every source on it changed state, and the server is the
   * only place that knows the result.
   */
  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      await captureAPI.ingestion.disconnectConnection();
      setDisconnectOpen(false);
      setJustDisconnected(true);
      await loadAll();
    } catch (err) {
      // 'not_connected' means someone else already did it. The page is stale,
      // not wrong to have asked: close, and show what is true now.
      if (err?.response?.data?.code === 'not_connected') {
        setDisconnectOpen(false);
        await loadAll();
        return;
      }
      setDisconnectError(err?.response?.data?.error || 'Microsoft 365 could not be disconnected.');
    } finally {
      setDisconnecting(false);
    }
  }, [loadAll]);

  /**
   * Add a source to the connection. NO trip to Microsoft: the tenant is already
   * connected, so this is one request, and the new source goes straight to the
   * step that finishes setting it up.
   */
  const handleAddSource = useCallback(async (provider) => {
    setConnectingId(provider.id);
    setConnectError(null);
    try {
      const res = await captureAPI.ingestion.createChannel(provider.id);
      const created = res?.channel;
      if (!created) {
        setConnectError('The source could not be added.');
        return;
      }
      setChannels((prev) => [...prev, created]);
      setConnectOpen(false);

      // MAILBOX FIRST, and the order is not cosmetic. The folder picker lists
      // folders IN a mailbox; opened before one is chosen it would call an
      // endpoint that refuses. A drive has no such step.
      if (isMailChannel(created.channel_type)) setMailboxChannel(created);
      else setPickerChannel(created);
    } catch (err) {
      // The server's own string: its 409s say whether to connect or reconnect.
      setConnectError(err?.response?.data?.error || 'The source could not be added.');
    } finally {
      setConnectingId(null);
    }
  }, []);

  /**
   * A mailbox was bound. The server returns the updated row; merging it rather
   * than refetching keeps the card from flickering through a loading state.
   *
   * REPLACED, NOT MERGED, when the binding changed: a re-bind clears the watch
   * folder, the cursor and the processed-folder cache server-side, and a spread
   * over the old local row would leave the stale watch folder on screen — the
   * one thing the admin most needs to see is gone.
   */
  const handleMailboxBound = useCallback((updated, info) => {
    if (!updated) return;
    setChannels((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));

    // Straight on to the folder step, which is what they came here to finish.
    // Only when there is no folder yet — a re-bind that cleared one lands here
    // too, and picking the new mailbox's folder is exactly the next thing.
    if (info?.rebound || updated.watch_folder_path == null) {
      setPickerChannel(updated);
    }
  }, []);

  const handlePickerClose = useCallback((info) => {
    setPickerChannel(null);
    // The dialog discovered the channel is gone. Its own 404 is the only signal
    // we get, so the list is refetched rather than patched.
    if (info && info.reason === 'channel_missing') loadAll();
  }, [loadAll]);

  const handlePickerSelected = useCallback((result) => {
    setChannels((prev) =>
      prev.map((c) =>
        c.id === pickerChannel?.id
          ? { ...c, watch_folder_path: result.watch_folder_path }
          : c,
      ),
    );
  }, [pickerChannel]);

  const m365Health = presentConnectionHealth(connection?.health_status);
  const m365Usable = isConnectionUsable(connection);

  if (loading) {
    return (
      <div className="ds-page">
        <div className="ds-loading">Loading document sources…</div>
      </div>
    );
  }

  return (
    <div className="ds-page">
      <div className="ds-header">
        <h2>Document Sources</h2>
        <p className="ds-subtitle">
          Cloud folders that documents arrive from automatically.
        </p>
      </div>

      {banner === 'success' && !sourcesUnattached && (
        <div className="ds-banner ds-banner-success">Microsoft 365 connected.</div>
      )}
      {/* Connected, but the sources that already existed were not attached to
          the connection, so they are not being checked. Amber rather than green:
          a card that only said "connected" would hide that. Reconnecting runs
          the attach again. */}
      {banner === 'success' && sourcesUnattached && (
        <div className="ds-banner ds-banner-warn">
          Microsoft 365 connected, but your existing sources could not be attached to it and
          are not being checked. Reconnect to try again.
        </div>
      )}
      {/* THE ONE FAILURE THE CALLBACK EXPLAINS. Signing in as a different
          Microsoft account is refused outright: nothing was changed. The account
          is named from the connection this page loaded itself — it is never in
          the URL — and when that read failed the sentence still says what
          happened without it. */}
      {banner === 'error' && failureReason === 'different_account' && (
        <div className="ds-banner ds-banner-error" role="alert">
          {connection?.account ? (
            <>
              This workspace is connected to Microsoft 365 as{' '}
              <strong>{connection.account}</strong>. You signed in as a different account, so
              nothing was changed. Sign in as <strong>{connection.account}</strong> to
              reconnect.
            </>
          ) : (
            <>
              You signed in as a different Microsoft account than the one this workspace is
              connected as, so nothing was changed. Sign in as the connected account to
              reconnect.
            </>
          )}
        </div>
      )}
      {/* Every other failure: the callback gives no cause on purpose — unknown,
          expired, consumed and another tenant's state are one case there — so
          this says no more than the server does. */}
      {banner === 'error' && failureReason !== 'different_account' && (
        <div className="ds-banner ds-banner-error">
          The connection could not be completed. Please try again.
        </div>
      )}

      {justDisconnected && !connection && (
        <div className="ds-banner ds-banner-success" role="status">
          Microsoft 365 disconnected. Its sources were turned off; documents already captured
          are not affected.
        </div>
      )}

      {loadError && <div className="ds-banner ds-banner-error">{loadError}</div>}

      {!isAdmin && (
        <div className="ds-note-card">
          <p className="ds-muted">Document sources are managed by your tenant admin.</p>
        </div>
      )}

      {/* ── Microsoft 365 ────────────────────────────────────────────────
          ONE connection for the workspace, above the sources that ride on it.
          Connect and Reconnect live here and nowhere else: a source has no
          sign-in of its own, so a Reconnect button on each card would be three
          buttons that all do the same thing to the same connection. */}
      {/* Its own class rather than ds-card: a ds-card is a SOURCE. */}
      <div className="ds-m365-card">
        <div className="ds-card-head">
          <div>
            <h3 className="ds-card-title">Microsoft 365</h3>
            <p className="ds-card-provider">
              One sign-in for OneDrive folders and Outlook mailboxes.
            </p>
          </div>
          {connection && (
            <span className={`ds-health ds-health-${m365Health.tone}`}>{m365Health.label}</span>
          )}
        </div>

        {!connectionKnown && (
          <p className="ds-muted">The Microsoft 365 connection could not be loaded.</p>
        )}

        {connectionKnown && !connection && (
          <p className="ds-muted">
            Not connected. Connect once, then add the folders and mailboxes documents arrive
            from.
          </p>
        )}

        {connection && (
          <>
            <p className="ds-m365-account">
              Signed in as <strong>{connection.account || 'an unknown account'}</strong>
            </p>
            {connection.health_status === 'reconnect_needed' && (
              <p className="ds-row-error">
                Microsoft is no longer accepting this sign-in, so no source is being checked.
                Reconnect as {connection.account || 'the connected account'} to resume.
              </p>
            )}
          </>
        )}

        {isAdmin && connectionKnown && (
          <div className="ds-reconnect-row">
            <button
              className={
                !connection || connection.health_status === 'reconnect_needed'
                  ? 'ds-btn-primary'
                  : 'ds-btn-secondary'
              }
              onClick={() => {
                setM365Error(null);
                setM365DialogOpen(true);
              }}
            >
              {!connection
                ? 'Connect Microsoft 365'
                : connection.health_status === 'reconnect_needed'
                  ? 'Reconnect'
                  : 'Reconnect\u2026'}
            </button>
            {/* Offered whenever there is a connection, working or not: a
                connection that needs reconnecting is exactly one somebody may
                prefer to remove. Never the prominent button. */}
            {connection && (
              <button className="ds-btn-secondary ds-btn-danger" onClick={openDisconnect}>
                Disconnect&hellip;
              </button>
            )}
          </div>
        )}
      </div>

      {cloudChannels.length === 0 ? (
        <div className="ds-empty">
          <p className="ds-empty-title">No document sources yet.</p>
          {isAdmin && (
            <button className="ds-btn-primary" onClick={() => setConnectOpen(true)}>
              Add a source
            </button>
          )}
        </div>
      ) : (
        <>
          {isAdmin && (
            <div className="ds-toolbar">
              <button className="ds-btn-primary" onClick={() => setConnectOpen(true)}>
                Add a source
              </button>
            </div>
          )}

          <div className="ds-list">
            {cloudChannels.map((channel) => {
              const provider = providerFor(channel.channel_type);
              const providerLabel = provider?.display_name || channel.channel_type;

              // ── A source that was TURNED OFF ─────────────────────────────
              // Listed, so a disconnect does not look like it deleted the
              // workspace's sources, and so what used to arrive from where is
              // still on record. Nothing on it can be changed: it is not being
              // checked, and connecting again does not switch it back on.
              if (channel.is_active === false) {
                const mailbox = isMailChannel(channel.channel_type) ? presentMailbox(channel) : null;
                return (
                  <div key={channel.id} className="ds-card ds-card-inactive">
                    <div className="ds-card-head">
                      <div>
                        <h3 className="ds-card-title">{channel.display_name}</h3>
                        <p className="ds-card-provider">{providerLabel}</p>
                      </div>
                      <span className="ds-health ds-health-neutral">Inactive</span>
                    </div>
                    <p className="ds-inert-note ds-disconnected-note">
                      Disconnected from Microsoft 365
                    </p>
                    {mailbox && <p className="ds-muted">Mailbox: {mailbox}</p>}
                    {channel.watch_folder_path && (
                      <p className="ds-muted">Was watching: {channel.watch_folder_path}</p>
                    )}
                  </div>
                );
              }

              // Unknown provider → inert, not enabled. A registry gap means we do
              // not know whether move is safe, and the honest answer to "we do not
              // know" is the same as "not yet".
              const canMove = provider?.capabilities?.move === true;
              const canDelete = provider?.capabilities?.delete === true;
              // The server's answer, both halves already ANDed: the provider can
              // move AND this channel's consent carries Files.ReadWrite. Never
              // recomputed here — the page would have to know about scopes to do
              // it, and there would then be two places that could disagree.
              const canDispose = channel.can_dispose === true;
              const isMail = isMailChannel(channel.channel_type);
              const mailboxLabel = presentMailbox(channel);
              /*
               * HIDDEN, NOT INERT — and the distinction is the whole reason this
               * is a separate flag from canDelete.
               *
               * An inert control with "coming with a future update" is the right
               * answer for a provider that is not finished: the capability is
               * absent today and present later. Mail is not that. Ruling 8 says
               * mail retention is the customer's mailbox policy and we will not
               * delete their email — there is no future update, and a greyed
               * "delete after N days" would promise one.
               *
               * KEYED ON THE CHANNEL BEING MAIL, not inferred from
               * `delete: false` plus `status: 'available'`. That pair does not
               * mean what it looks like: OneDrive was available with delete
               * false for the whole of C1, and rendered the inert control
               * correctly. The note this flag controls is literally about a
               * MAILBOX's retention policy, so mail is what it should ask about.
               *
               * `!canDelete` stays in the condition so that if mail ever gains a
               * delete capability the control comes back rather than staying
               * hidden by a rule nobody revisits.
               */
              const deletionWithheld = isMail && !canDelete;
              const health = presentHealth(channel.health_status);
              /*
               * TWO CLOCKS, TWO LABELS, AND THEY ARE NOT THE SAME FACT.
               *
               * `delta_cursor_at` is written by persistCursor on every
               * enumeration that reaches a deltaLink — including an enumeration
               * that found nothing — so it answers the operator's question:
               * did it look?
               *
               * `last_success_at` is written only when the channel's OAuth
               * token was actually REFRESHED, which is at most once per token
               * lifetime. It therefore lags a perfectly healthy channel by tens
               * of minutes as a matter of design, not of failure.
               *
               * This card used to render `last_success_at` under the words
               * "Last check", and that single mislabel is the whole of the
               * 2026-09-07 "the connector is not working" incident: the page
               * said the source was checked 26 minutes ago about a source it
               * had enumerated 40 seconds earlier.
               *
               * Both fall back to "never" rather than being hidden. A blank
               * where a timestamp belongs reads as a rendering bug; "never" is
               * a fact, and on a channel that has no watch folder yet it is the
               * correct one.
               */
              const lastEnumerated = formatRelative(channel.delta_cursor_at) || 'never';
              // For a Microsoft source the sign-in that was verified is the
              // connection's, so the connection's clock is read directly — the
              // row a settings PATCH hands back carries the channel's own
              // column, which no longer moves, and would overwrite the list's.
              const connectionVerified =
                formatRelative(
                  usesM365Connection(channel.channel_type) && connection
                    ? connection.last_success_at
                    : channel.last_success_at,
                ) || 'never';
              /*
               * B2. `undefined` (not answered yet, or the read failed) and a
               * resolved-but-empty answer are the same on the card: "none yet".
               * Distinguishing them would mean a spinner on a line that is
               * reassurance rather than a control, and a permanent "loading…"
               * on a failed read reads worse than a fact that happens to be
               * "nothing yet".
               */
              const channelActivity = activity[channel.id];
              const lastItem = channelActivity?.lastItem || null;
              const lastItemSeen = lastItem ? formatRelative(lastItem.seen_at) : null;
              const duplicatesHeld = channelActivity?.duplicatesHeld ?? 0;
              const saving = savingId === channel.id;
              const rowError = rowErrors[channel.id];

              return (
                <div key={channel.id} className="ds-card">
                  <div className="ds-card-head">
                    <div>
                      <h3 className="ds-card-title">{channel.display_name}</h3>
                      <p className="ds-card-provider">{providerLabel}</p>
                    </div>
                    <span className={`ds-health ds-health-${health.tone}`}>{health.label}</span>
                  </div>

                  {/* EVERY HEALTH STATE, not just 'connected'.

                      The single line this replaced was held to a connected
                      channel, and inheriting that gate was wrong. On a broken
                      channel these two are not stale noise beside the Reconnect
                      button — they are the evidence for what broke and when.
                      "Last checked for new files: 3 days ago / Connection last
                      verified: 3 days ago" says the credential died and took the
                      enumeration with it; "checked 40 seconds ago / verified 3
                      days ago" says the opposite, and the difference decides
                      whether reconnecting is the fix. Hiding both in exactly the
                      state where someone is diagnosing is the same failure this
                      card keeps being fixed for: withholding the fact that
                      answers the question.

                      Both already fall back to "never", so a channel that has
                      never succeeded at either renders honestly rather than
                      blank. */}
                  <p className="ds-last-check">
                    Last checked for new files: {lastEnumerated}
                  </p>
                  <p className="ds-last-check ds-last-check--muted">
                    Connection last verified: {connectionVerified}
                  </p>

                  {/* STILL held to a connected channel, deliberately, and the
                      contrast with the two lines above is the point. Those are
                      diagnostic — they describe the connection itself, which is
                      what a broken card is about. These two describe the flow of
                      documents through a channel that is working, and on a
                      reconnect_needed card "Last item seen: none yet" invites
                      the reading that nothing ever arrived rather than that
                      nothing can arrive right now.

                      Within a connected channel they render unconditionally:
                      "none yet" and "0" are answers, and a line that appears
                      only when there is something to say cannot be used to check
                      that there is not. */}
                  {channel.health_status === 'connected' && (
                    <>
                      <p className="ds-last-check">
                        Last item seen:{' '}
                        {lastItem
                          ? `${lastItem.name}${lastItemSeen ? `, ${lastItemSeen}` : ''}`
                          : 'none yet'}
                      </p>
                      <p className="ds-last-check">
                        Duplicates held:{' '}
                        {/* Amber ONLY when there is something held. Colouring a
                            zero would make every healthy channel look like it
                            needed attention, which is how a warning colour stops
                            meaning anything.

                            PLAIN TEXT, NOT A LINK. The decision asked for the
                            count to link to the Processing Monitor filtered to
                            this channel. There is no such filter: the route
                            accepts batch_id, status, since and limit only, and
                            does not even select ingestion_channel_id
                            (processingMonitor.ts). A link to an unfiltered list
                            would be worse than none — it would promise a filter
                            the page does not apply. Revisit when B4 adds the
                            Held tab, which is where this number belongs. */}
                        <span className={duplicatesHeld > 0 ? 'ds-held-count' : undefined}>
                          {duplicatesHeld}
                        </span>
                      </p>
                    </>
                  )}

                  {/* There is NO Reconnect here. A source has no sign-in of its
                      own; the Microsoft 365 card above is the one place to
                      reconnect. What a source card owes the reader is the
                      reason it has gone quiet, when the reason is not its own. */}
                  {usesM365Connection(channel.channel_type) && connectionKnown && (
                    <>
                      {!connection && (
                        <p className="ds-inert-note ds-paused-note">
                          Paused. Connect Microsoft 365 above to resume.
                        </p>
                      )}
                      {connection && !m365Usable && (
                        <p className="ds-inert-note ds-paused-note">
                          Paused. Reconnect Microsoft 365 above to resume.
                        </p>
                      )}
                      {connection && m365Usable && channel.attached === false && (
                        <p className="ds-inert-note ds-paused-note">
                          Not attached to the Microsoft 365 connection, so it is not being
                          checked. Reconnect Microsoft 365 above to attach it.
                        </p>
                      )}
                    </>
                  )}

                  {/* Read from the list's resolved binding, NEVER derived from
                      health_status. 'orphaned' was revised out as a health value
                      — it lives in the poller's UNPOLLED_STATES, so writing it
                      would stop the credential refresh on a channel an admin is
                      still setting up. Bound-ness and connection health are two
                      independent facts and are shown as two. */}
                  <div className="ds-field">
                    <span className="ds-field-label">Workflow</span>
                    <span className="ds-field-value">
                      {channel.workflow_name || (
                        <span className="ds-muted">Not linked to a workflow</span>
                      )}
                    </span>
                  </div>

                  {/* Only once a move has actually happened — the path is
                      written when the processed folder is first resolved, so its
                      presence is evidence the folder exists in the drive rather
                      than a promise that it will. */}
                  {channel.processed_folder_path && (
                    <div className="ds-field">
                      <span className="ds-field-label">Processed documents</span>
                      <span className="ds-field-value">
                        Processed documents go to {channel.processed_folder_path}
                      </span>
                    </div>
                  )}

                  {/* B3, and gated on its own path for the same reason as the
                      line above: the path is written when the Duplicates folder
                      is first resolved in the drive, so its presence is evidence
                      the folder exists rather than a promise that it will.

                      SEPARATE FROM the Processed line, not appended to it. The
                      two folders are resolved independently and a channel can
                      easily have one and not the other — every channel moving
                      documents today has a Processed folder and no Duplicates
                      folder, and will until it first holds a duplicate.

                      The pairing is the operator-facing half of the whole slice:
                      Processed = captured, Duplicates = seen, held, not captured
                      again. Learned once, from two lines that look alike. */}
                  {channel.duplicates_folder_path && (
                    <div className="ds-field">
                      <span className="ds-field-label">Duplicates</span>
                      <span className="ds-field-value">
                        Duplicates go to {channel.duplicates_folder_path}
                      </span>
                    </div>
                  )}

                  {/* Mail only. A drive channel's target is decided by whose
                      account authorised the consent; a mail consent covers the
                      user's own mailbox AND every shared one they can reach, so
                      which mailbox is a separate, explicit answer. */}
                  {isMail && (
                    <div className="ds-field">
                      <span className="ds-field-label">Mailbox</span>
                      <span className="ds-field-value">
                        {mailboxLabel || (
                          <span className="ds-muted">No mailbox chosen yet</span>
                        )}
                      </span>
                      {isAdmin && (
                        <button
                          className="ds-btn-secondary"
                          onClick={() => setMailboxChannel(channel)}
                        >
                          {mailboxLabel ? 'Change mailbox' : 'Choose mailbox'}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="ds-field">
                    <span className="ds-field-label">Watching</span>
                    <span className="ds-field-value">
                      {channel.watch_folder_path || (
                        <span className="ds-muted">
                          {isMail && !mailboxLabel
                            ? 'Choose a mailbox first'
                            : 'No folder selected'}
                        </span>
                      )}
                    </span>
                    {isAdmin && (
                      <button
                        className="ds-btn-secondary"
                        /* Disabled until a mailbox exists: the folders endpoint
                           refuses a mail channel with no mailbox, so the dialog
                           would open onto an error. */
                        disabled={isMail && !mailboxLabel}
                        onClick={() => setPickerChannel(channel)}
                      >
                        Change folder
                      </button>
                    )}
                  </div>

                  {isAdmin && (
                    <>
                      <div className="ds-field">
                        <label className="ds-field-label" htmlFor={`ds-freq-${channel.id}`}>
                          Check for new documents
                        </label>
                        {/* Options come from the server's enumeration_intervals.
                            A literal list here could offer a cadence the PATCH
                            validator rejects, and the whole point of serving the
                            options from the constant the validator uses is that
                            it cannot. */}
                        <select
                          id={`ds-freq-${channel.id}`}
                          className="ds-select"
                          value={String(channel.enumeration_interval_seconds ?? '')}
                          disabled={saving}
                          onChange={(e) =>
                            patchChannel(channel.id, {
                              enumeration_interval_seconds: parseInt(e.target.value, 10),
                            })
                          }
                        >
                          {intervals.map((opt) => (
                            <option key={opt.seconds} value={String(opt.seconds)}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="ds-disposition">
                        <label className="ds-toggle-label">
                          <input
                            type="checkbox"
                            className="ds-toggle-input"
                            checked={Boolean(channel.move_processed)}
                            disabled={saving || !canMove || !canDispose}
                            onChange={(e) =>
                              patchChannel(channel.id, { move_processed: e.target.checked })
                            }
                          />
                          <span className="ds-toggle-switch" />
                          <span className="ds-toggle-text">
                            Move to processed folder after capture
                          </span>
                        </label>

                        {!deletionWithheld && (
                        <div className="ds-field">
                          <label className="ds-field-label" htmlFor={`ds-del-${channel.id}`}>
                            Delete from processed folder after
                          </label>
                          {/* No pre-fill: null shows Never. Disabled whenever move
                              is off — a client mirror of the server's cross-field
                              rule, which is still the thing that enforces it. */}
                          <select
                            id={`ds-del-${channel.id}`}
                            className="ds-select"
                            value={
                              channel.delete_after_days == null
                                ? ''
                                : String(channel.delete_after_days)
                            }
                            disabled={
                              saving || !canMove || !canDispose || !canDelete ||
                              !channel.move_processed
                            }
                            onChange={(e) =>
                              patchChannel(channel.id, {
                                delete_after_days:
                                  e.target.value === '' ? null : parseInt(e.target.value, 10),
                              })
                            }
                          >
                            <option value="">Never</option>
                            {DELETE_AFTER_DAYS_OPTIONS.map((days) => (
                              <option key={days} value={String(days)}>
                                {days} days
                              </option>
                            ))}
                          </select>
                        </div>
                        )}

                        {/* Said once, plainly, instead of a greyed control. A
                            person who wants their mail tidied after N days has a
                            mailbox retention policy for that, and it is theirs
                            to set — not something this product should imply it
                            is about to take over. */}
                        {deletionWithheld && (
                          <p className="ds-inert-note">
                            Handled mail is moved, never deleted. Deleting mail is left to your
                            mailbox&rsquo;s own retention policy.
                          </p>
                        )}

                        {/* Present-but-inert, never hidden. The API would accept
                            these writes; the connector cannot act on them yet
                            because the held scope is Files.Read. Saying so is the
                            honest form — hiding them would imply the feature does
                            not exist, and enabling them would imply it works. */}
                        {/* TWO REASONS THESE ARE INERT, and they need different
                            sentences because they need different actions.

                            The provider cannot move at all yet (registry) — the
                            admin can do nothing about that and should not be
                            invited to try.

                            The provider CAN move but this channel's consent
                            predates the write scope — one reconnect fixes it,
                            and saying "coming with a future update" would be
                            false and would leave them waiting for us.

                            Registry `move` is still false in C1, so the first
                            branch is what shows today; the second goes live the
                            moment C2 flips one constant. */}
                        {!canMove && (
                          <p className="ds-inert-note">
                            Not yet available for {providerLabel}. Requires additional Microsoft
                            permissions (coming with a future update).
                          </p>
                        )}
                        {canMove && !canDispose && (
                          <p className="ds-inert-note">
                            Reconnect Microsoft 365, above, to grant the permission this needs.
                          </p>
                        )}
                        {canMove && canDispose && !canDelete && !deletionWithheld && (
                          <p className="ds-inert-note">
                            Deleting is not yet available for {providerLabel}. Requires additional
                            Microsoft permissions (coming with a future update).
                          </p>
                        )}
                      </div>

                      {/* ── Import settings (mail only) ──────────────────
                          What counts as a document in an email. Every control
                          here has a consequence a person can picture, which is
                          why the thresholds are tucked behind a disclosure
                          rather than sitting beside the switches: "ignore small
                          inline images" is the decision, and "600px, 1MP" is the
                          tuning of it. */}
                      {isMail && (
                        <div className="ds-import-settings">
                          <h4 className="ds-subhead">Import settings</h4>

                          <label className="ds-toggle-label">
                            <input
                              type="checkbox"
                              className="ds-toggle-input"
                              checked={channel.require_attachments !== false}
                              disabled={saving}
                              onChange={(e) =>
                                patchChannel(channel.id, { require_attachments: e.target.checked })
                              }
                            />
                            <span className="ds-toggle-switch" />
                            <span className="ds-toggle-text">
                              Only import messages that have something attached
                            </span>
                          </label>

                          <label className="ds-toggle-label">
                            <input
                              type="checkbox"
                              className="ds-toggle-input"
                              checked={channel.include_inline_images !== false}
                              disabled={saving}
                              onChange={(e) =>
                                patchChannel(channel.id, {
                                  include_inline_images: e.target.checked,
                                })
                              }
                            />
                            <span className="ds-toggle-switch" />
                            <span className="ds-toggle-text">
                              Import images pasted into the message body
                            </span>
                          </label>
                          <p className="ds-field-note">
                            A photo of a handwritten invoice usually arrives this way. Small
                            images such as signature logos are ignored.
                          </p>

                          {/* ADVANCED, and closed by default. These two numbers
                              were set from three test messages; they are
                              adjustable for exactly that reason, and every
                              decision they drive is recorded per attachment so
                              they can be re-derived from real mail. Nobody
                              should have to understand them to use the product. */}
                          {channel.include_inline_images !== false && (
                            <details className="ds-advanced">
                              <summary className="ds-advanced-summary">
                                Advanced: what counts as too small
                              </summary>
                              <div className="ds-advanced-body">
                                <div className="ds-field">
                                  <label
                                    className="ds-field-label"
                                    htmlFor={`ds-shortside-${channel.id}`}
                                  >
                                    Shortest side, at least
                                  </label>
                                  <input
                                    id={`ds-shortside-${channel.id}`}
                                    className="ds-number-input"
                                    type="number"
                                    min="1"
                                    step="1"
                                    defaultValue={channel.min_inline_short_side_px ?? 600}
                                    disabled={saving}
                                    /* onBlur, not onChange: this is a free-text
                                       number and a PATCH per keystroke would
                                       send "6", "60", "600" and reject the first
                                       two with an error the admin sees flash. */
                                    onBlur={(e) => {
                                      const value = parseInt(e.target.value, 10);
                                      if (!Number.isFinite(value)) return;
                                      if (value === channel.min_inline_short_side_px) return;
                                      patchChannel(channel.id, {
                                        min_inline_short_side_px: value,
                                      });
                                    }}
                                  />
                                  <span className="ds-field-suffix">pixels</span>
                                </div>

                                <div className="ds-field">
                                  <label
                                    className="ds-field-label"
                                    htmlFor={`ds-megapixels-${channel.id}`}
                                  >
                                    Total size, at least
                                  </label>
                                  <input
                                    id={`ds-megapixels-${channel.id}`}
                                    className="ds-number-input"
                                    type="number"
                                    min="0.1"
                                    step="0.1"
                                    defaultValue={channel.min_inline_megapixels ?? 1}
                                    disabled={saving}
                                    onBlur={(e) => {
                                      const value = parseFloat(e.target.value);
                                      if (!Number.isFinite(value)) return;
                                      if (value === channel.min_inline_megapixels) return;
                                      patchChannel(channel.id, {
                                        min_inline_megapixels: value,
                                      });
                                    }}
                                  />
                                  <span className="ds-field-suffix">megapixels</span>
                                </div>

                                <p className="ds-field-note">
                                  An image must clear both to be imported.
                                </p>
                              </div>
                            </details>
                          )}

                          <div className="ds-field ds-field-block">
                            <span className="ds-field-label">Attachment types to import</span>
                            <div className="ds-checkbox-row">
                              {ACCEPTED_MIME_TYPE_OPTIONS.map((option) => {
                                const current = Array.isArray(channel.accepted_mime_types)
                                  ? channel.accepted_mime_types
                                  : [];
                                const checked = current.includes(option.value);
                                // Unchecking the last one is refused by the
                                // server ("a channel that accepts nothing imports
                                // nothing"). Disabled here so the refusal is not
                                // the way an admin discovers it.
                                const isLast = checked && current.length === 1;
                                return (
                                  <label className="ds-checkbox-label" key={option.value}>
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      disabled={saving || isLast}
                                      onChange={(e) => {
                                        const next = e.target.checked
                                          ? [...current, option.value]
                                          : current.filter((t) => t !== option.value);
                                        patchChannel(channel.id, { accepted_mime_types: next });
                                      }}
                                    />
                                    <span>{option.label}</span>
                                  </label>
                                );
                              })}
                            </div>
                            {/* A channel configured by hand may accept a type
                                this build does not offer. Shown rather than
                                silently dropped on the next save — the
                                checkboxes only ever add or remove their own
                                value, so anything else survives. */}
                            {Array.isArray(channel.accepted_mime_types) &&
                              channel.accepted_mime_types.some(
                                (t) => !ACCEPTED_MIME_TYPE_OPTIONS.some((o) => o.value === t),
                              ) && (
                                <p className="ds-field-note">
                                  Also importing:{' '}
                                  {channel.accepted_mime_types
                                    .filter(
                                      (t) =>
                                        !ACCEPTED_MIME_TYPE_OPTIONS.some((o) => o.value === t),
                                    )
                                    .join(', ')}
                                </p>
                              )}
                          </div>
                        </div>
                      )}

                      {savedId === channel.id && (
                        <span className="ds-saved-indicator">&#10003; Saved</span>
                      )}
                      {rowError && <p className="ds-row-error">{rowError}</p>}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {connectOpen && isAdmin && (
        <div className="ds-overlay" onClick={() => setConnectOpen(false)}>
          <div className="ds-connect-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="ds-connect-head">
              <h3>Add a source</h3>
              <button className="ds-close-btn" onClick={() => setConnectOpen(false)}>
                &times;
              </button>
            </div>

            {/* Said ONCE, above the tiles, rather than under each: every tile is
                disabled for the same reason and the fix is the same card. */}
            {!m365Usable && (
              <p className="ds-provider-reason ds-connect-first">
                {connection
                  ? 'Reconnect Microsoft 365 first. Sources can be added once it is working again.'
                  : 'Connect Microsoft 365 first. Sources are added to that connection.'}
              </p>
            )}

            {connectError && <p className="ds-row-error">{connectError}</p>}

            <div className="ds-provider-list">
              {providers.map((provider) => {
                const available = provider.status === 'available';
                // Every provider here rides on the Microsoft 365 connection, so
                // none can be added without a usable one.
                const enabled = available && m365Usable;
                return (
                  <div
                    key={provider.id}
                    className={`ds-provider${enabled ? '' : ' ds-provider-disabled'}`}
                  >
                    <button
                      className="ds-provider-btn"
                      disabled={!enabled || connectingId !== null}
                      onClick={() => handleAddSource(provider)}
                    >
                      {provider.display_name}
                      {connectingId === provider.id && <span className="ds-muted"> — adding…</span>}
                    </button>
                    {/* Shown rather than hidden: a picker with one row looks
                        broken, and a greyed row with no reason is a support
                        ticket. */}
                    {!available && provider.unavailable_reason && (
                      <p className="ds-provider-reason">{provider.unavailable_reason}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* The consent screen lists everything in one go and will NOT say why.
          Explain it here, before they get there — otherwise the honest reaction
          to "have full access to your files" is to cancel. It is ONE screen for
          files and mail together now, so this names both. */}
      {m365DialogOpen && isAdmin && (
        <div className="ds-overlay" onClick={() => (m365Starting ? null : setM365DialogOpen(false))}>
          <div className="ds-connect-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="ds-connect-head">
              <h3>{connection ? 'Reconnect Microsoft 365' : 'Connect Microsoft 365'}</h3>
              <button
                className="ds-close-btn"
                onClick={() => setM365DialogOpen(false)}
                disabled={m365Starting}
              >
                &times;
              </button>
            </div>

            <p className="ds-dialog-copy">
              You will be sent to Microsoft to sign in. One sign-in covers every OneDrive folder
              and Outlook mailbox you add here.
            </p>
            {connection && (
              <p className="ds-dialog-copy">
                Sign in as <strong>{connection.account || 'the connected account'}</strong>. A
                different account will be refused and nothing will change.
              </p>
            )}
            {!connection && (
              <p className="ds-dialog-copy">
                Sources can reach what the account you sign in with can reach: its own OneDrive
                and mailbox, and any shared mailbox it has full access to. A dedicated account
                keeps that separate from a person&rsquo;s own files and mail.
              </p>
            )}
            <p className="ds-dialog-copy">
              Microsoft will ask for permission to <strong>read and write</strong> files and
              mail. Writing is what lets a captured document or message be moved to a processed
              folder, so it is not imported twice.
            </p>
            <p className="ds-dialog-copy">
              <strong>Nothing is deleted unless you turn deletion on yourself</strong>, mail is
              never deleted, and no mail is ever sent.
            </p>

            {m365Error && <p className="ds-row-error">{m365Error}</p>}

            <div className="ds-dialog-actions">
              <button
                className="ds-btn-secondary"
                onClick={() => setM365DialogOpen(false)}
                disabled={m365Starting}
              >
                Cancel
              </button>
              <button className="ds-btn-primary" onClick={handleM365Start} disabled={m365Starting}>
                {m365Starting ? 'Starting\u2026' : 'Continue to Microsoft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Disconnect ───────────────────────────────────────────────────
          The app's own dialog, never window.confirm: it has to SHOW something
          — the sources that will stop — before it asks. The confirm button
          stays disabled until that list has loaded. */}
      {disconnectOpen && isAdmin && (
        <div className="ds-overlay" onClick={() => (disconnecting ? null : setDisconnectOpen(false))}>
          <div
            className="ds-connect-dialog ds-disconnect-dialog"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ds-disconnect-title"
          >
            <div className="ds-connect-head">
              <h3 id="ds-disconnect-title">Disconnect Microsoft 365</h3>
              <button
                className="ds-close-btn"
                onClick={() => setDisconnectOpen(false)}
                disabled={disconnecting}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {!disconnectPreview && !disconnectError && (
              <p className="ds-muted">Loading what this will turn off&hellip;</p>
            )}

            {disconnectPreview && (
              <>
                <p className="ds-dialog-copy">
                  This removes the sign-in for{' '}
                  <strong>{disconnectPreview.account || connection?.account || 'the connected account'}</strong>{' '}
                  from this workspace.
                </p>
                {disconnectPreview.sources.length > 0 ? (
                  <>
                    <p className="ds-dialog-copy">
                      {disconnectPreview.sources.length === 1
                        ? 'This source will be turned off and will stop bringing in documents:'
                        : 'These sources will be turned off and will stop bringing in documents:'}
                    </p>
                    <ul className="ds-disconnect-sources">
                      {disconnectPreview.sources.map((source) => (
                        <li key={source.id}>
                          <strong>{source.display_name}</strong>
                          <span className="ds-muted">
                            {' '}&mdash; {providerFor(source.channel_type)?.display_name || source.channel_type}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="ds-dialog-copy">No sources are using this connection.</p>
                )}
                {disconnectPreview.destinations.length > 0 && (
                  <>
                    <p className="ds-dialog-copy">
                      {disconnectPreview.destinations.length === 1
                        ? 'This publish destination will be turned off. Workflows that publish to it will fail until it is reactivated or another destination is chosen:'
                        : 'These publish destinations will be turned off. Workflows that publish to them will fail until they are reactivated or other destinations are chosen:'}
                    </p>
                    <ul className="ds-disconnect-destinations">
                      {disconnectPreview.destinations.map((destination) => (
                        <li key={destination.id}>
                          <strong>{destination.name}</strong>
                          <span className="ds-muted"> &mdash; OneDrive destination</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <p className="ds-dialog-copy">
                  <strong>Documents already captured are not affected.</strong> Nothing is deleted
                  from OneDrive or from any mailbox.
                </p>
                <p className="ds-dialog-copy">
                  Connecting again later does not turn these sources back on; add the sources you
                  need again after connecting.
                  {disconnectPreview.destinations.length > 0 &&
                    ' A destination can be reactivated in Destinations once Microsoft 365 is connected again.'}
                </p>
                <p className="ds-dialog-copy ds-muted">
                  This does not sign you out of Microsoft. If you also want to remove this
                  app&rsquo;s access at Microsoft, you can do that from the apps and permissions
                  page of your Microsoft account.
                </p>
              </>
            )}

            {disconnectError && <p className="ds-row-error" role="alert">{disconnectError}</p>}

            <div className="ds-dialog-actions">
              <button
                className="ds-btn-secondary"
                onClick={() => setDisconnectOpen(false)}
                disabled={disconnecting}
              >
                Cancel
              </button>
              <button
                className="ds-btn-primary ds-btn-danger-solid"
                onClick={handleDisconnect}
                disabled={disconnecting || !disconnectPreview}
              >
                {disconnecting ? 'Disconnecting\u2026' : 'Disconnect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rendered BEFORE the folder picker in the tree as well as in the flow.
          Both can be open in sequence — binding a mailbox opens the picker — and
          keeping the order here matching the order on screen is one less thing
          to reason about when they interact. */}
      {mailboxChannel && isAdmin && (
        <MailboxStepDialog
          channel={mailboxChannel}
          onClose={() => setMailboxChannel(null)}
          onBound={handleMailboxBound}
        />
      )}

      {pickerChannel && isAdmin && (
        <CloudFolderPickerDialog
          channelId={pickerChannel.id}
          currentPath={pickerChannel.watch_folder_path}
          onClose={handlePickerClose}
          onSelected={handlePickerSelected}
        />
      )}
    </div>
  );
}
